import { DbRef, orderedDaysOfWeek } from '@caresend/types';
import { getPath } from './path.js';
import { getDayOfWeek, getSpecificTimeOnDate } from '../formatters/date.js';
import { initTimeZoneDate } from '../initializers/date.js';

/**
 * ```
 * `${DbRef.PLACES}/${id}/${childPath}`
 * ```
 */

function getPlacePath(id, childPath) {
  return getPath(`${DbRef.PLACES}/${id}`, childPath);
}
/**
 * Examples:
 *
 * ```
 * getDaysOfWeekInRange(DayOfWeek.MONDAY, DayOfWeek.MONDAY)
 * ⬇️
 * [DayOfWeek.MONDAY]
 * ```
 *
 * ```
 * getDaysOfWeekInRange(DayOfWeek.MONDAY, DayOfWeek.TUESDAY)
 * ⬇️
 * [DayOfWeek.MONDAY, DayOfWeek.TUESDAY]
 * ```
 *
 * ```
 * getDaysOfWeekInRange(DayOfWeek.THURSDAY, DayOfWeek.MONDAY)
 * ⬇️
 * [
 *   DayOfWeek.THURSDAY,
 *   DayOfWeek.FRIDAY,
 *   DayOfWeek.SATURDAY,
 *   DayOfWeek.SUNDAY,
 *   DayOfWeek.MONDAY,
 * ]
 * ```
 */

const getDaysOfWeekInRange = (dayFrom, dayTo) => {
  const startIndex = orderedDaysOfWeek.indexOf(dayFrom);
  const endIndex = orderedDaysOfWeek.indexOf(dayTo);

  if (startIndex === endIndex) {
    return [dayFrom];
  }

  if (startIndex < endIndex) {
    return orderedDaysOfWeek.slice(startIndex, endIndex + 1);
  }

  return [...orderedDaysOfWeek.slice(startIndex), ...orderedDaysOfWeek.slice(0, endIndex + 1)];
};
/**
 * Filters the opening hours list to only include segments that apply to a given
 * day.
 *
 * Examples for Lab open Tue-Thur 8a-12p, Tue-Tue 2p-4p:
 *
 * ```
 * getOpeningHoursForDay(DayOfWeek.TUESDAY, openingHours)
 * ⬇️
 * [
 *   { dayFrom: DayOfWeek.TUESDAY, dayTo: DayOfWeek.THURSDAY, timeFrom: '800', timeTo: '1200' },
 *   { dayFrom: DayOfWeek.TUESDAY, dayTo: DayOfWeek.TUESDAY, timeFrom: '1400', timeTo: '1600' },
 * ]
 * ```
 *
 * ```
 * getOpeningHoursForDay(DayOfWeek.WEDNESDAY, openingHours)
 * ⬇️
 * [
 *   { dayFrom: DayOfWeek.TUESDAY, dayTo: DayOfWeek.THURSDAY, timeFrom: '800', timeTo: '1200' },
 * ]
 * ```
 *
 * ```
 * getOpeningHoursForDay(DayOfWeek.MONDAY, openingHours)
 * ⬇️
 * []
 * ```
 */

const getOpeningHoursForDay = (dayOfWeek, openingHours) => openingHours.filter(openingHour => {
  const daysInRange = getDaysOfWeekInRange(openingHour.dayFrom, openingHour.dayTo);
  return daysInRange.includes(dayOfWeek);
});
/**
 * Returns the earliest opening time for a given day.
 *
 * Examples for Lab open Tue-Thur 8a-12p, Tue-Tue 2p-4p:
 *
 * ```
 * getOpeningTimeForDay(DayOfWeek.TUESDAY, openingHours)
 * ⬇️
 * '0800'
 * ```
 *
 * ```
 * getOpeningTimeForDay(DayOfWeek.WEDNESDAY, openingHours)
 * ⬇️
 * '0800'
 * ```
 *
 * ```
 * getOpeningTimeForDay(DayOfWeek.MONDAY, openingHours)
 * ⬇️
 * null
 * ```
 */

const getOpeningTimeForDay = (dayOfWeek, openingHours) => {
  const openingHoursForDay = getOpeningHoursForDay(dayOfWeek, openingHours);
  const allOpeningTimeNumbers = openingHoursForDay.map(({
    timeFrom
  }) => parseInt(timeFrom, 10));
  if (!allOpeningTimeNumbers.length) return null;
  const earliestOpeningTimeNumber = Math.min(...allOpeningTimeNumbers);
  const earliestOpeningTime = earliestOpeningTimeNumber.toString();
  return earliestOpeningTime;
};
/**
 * Returns the opening `TimeZoneDate` for a given place on a given date.
 *
 * Examples for Lab open Tue-Thur 8a-12p, Tue-Tue 2p-4p:
 *
 * ```
 * // Tuesday, 10/13/2026 23:46
 * getPlaceOpeningTimeOnDate(mockPlace(), MOCK_TIMESTAMP_FUTURE + DAY * 3)
 * ⬇️
 * {
 *   timeZone: 'America/Los_Angeles',
 *   timestamp: 1791903600000, // 10/13/2026 8:00 am
 * }
 * ```
 *
 * ```
 * // Saturday
 * getPlaceOpeningTimeOnDate(mockPlace(), MOCK_TIMESTAMP_FUTURE)
 * ⬇️
 * null
 * ```
 *
 *
 */

const getPlaceOpeningTimeOnDate = (place, timestamp) => {
  const {
    location,
    openingHours
  } = place;
  const {
    timeZone
  } = location;
  if (!timeZone) throw Error(`Place ${place.id} has no time zone`);
  const timeZoneDate = initTimeZoneDate(timeZone, timestamp);
  const dayOfWeek = getDayOfWeek(timeZoneDate);
  const openingTime = getOpeningTimeForDay(dayOfWeek, openingHours !== null && openingHours !== void 0 ? openingHours : []);
  if (!openingTime) return null;
  const openingTimeOnDate = getSpecificTimeOnDate(timeZoneDate, openingTime);
  return openingTimeOnDate;
};

export { getDaysOfWeekInRange, getOpeningHoursForDay, getOpeningTimeForDay, getPlaceOpeningTimeOnDate, getPlacePath };
